/**
 * Router de hash mínimo con parámetros (el useHashSection del frontend AML no
 * lleva params; aquí la ficha y la red van dirigidas por id de entidad):
 *   #/panorama · #/explorador · #/entidad/<uuid> · #/red/<uuid>/<saltos>
 *   #/registro/<corrida>/<persona>: la ficha de una persona o empresa del Registro Público de Comercio (etapa 4).
 *   #/representante/<uuid>: la ficha de un representante ante el SAT sin ficha confirmada (corte 4a de los certificados del SAT).
 * Sin React (la parte pura de rutas.ts), para probarla con Deno.
 */

export type Ruta =
  | { vista: "panorama" }
  | { vista: "explorador" }
  | { vista: "entidad"; id: string; actor?: string }
  /** Etapa 4: la ficha (nexo.hallazgos) de una persona o empresa del registro, en su corrida. */
  | { vista: "registro"; id: string; runId: string }
  /** Corte 4a de los certificados del SAT: la ficha de un representante sin ficha confirmada (sat_rep), por su uuid (nunca su CURP). */
  | { vista: "representante"; id: string }
  | { vista: "red"; id: string; saltos: number }
  | { vista: "cruce" }
  | { vista: "justicia" }
  | { vista: "documentos" }
  | { vista: "documento"; id: string }
  | { vista: "revisar"; id: string };

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Los mismos patrones que valida la base para `siger_actor` (nexo.hallazgos_sujeto). */
const ACTOR_RE = /^[A-Za-z0-9_-]{1,128}$/;
const CORRIDA_RE = /^[A-Za-z0-9_.-]{1,160}$/;

export function rutaAHash(r: Ruta): string {
  switch (r.vista) {
    case "panorama":
      return "#/panorama";
    case "explorador":
      return "#/explorador";
    case "entidad":
      return r.actor ? `#/entidad/${r.id}/actor/${encodeURIComponent(r.actor)}` : `#/entidad/${r.id}`;
    case "registro":
      return `#/registro/${encodeURIComponent(r.runId)}/${encodeURIComponent(r.id)}`;
    case "representante":
      return `#/representante/${r.id}`;
    case "red":
      return `#/red/${r.id}/${String(r.saltos)}`;
    case "cruce":
      return "#/cruce";
    case "justicia":
      return "#/justicia";
    case "documentos":
      return "#/documentos";
    case "documento":
      return `#/documento/${encodeURIComponent(r.id)}`;
    case "revisar":
      return `#/revisar/${encodeURIComponent(r.id)}`;
  }
}

/** decodeURIComponent sin lanzar con un «%» mal formado (un hash escrito a mano). */
function decodificar(x: string): string | null {
  try {
    return decodeURIComponent(x);
  } catch {
    return null;
  }
}

export function parseHash(hash: string): Ruta {
  const partes = hash.replace(/^#\/?/, "").split("/");
  const [vista, id, extra] = partes;
  if (vista === "explorador") return { vista: "explorador" };
  if (vista === "cruce") return { vista: "cruce" };
  if (vista === "justicia") return { vista: "justicia" };
  if (vista === "documentos") return { vista: "documentos" };
  if (vista === "documento" && id) return { vista: "documento", id: decodeURIComponent(id) };
  if (vista === "revisar" && id) return { vista: "revisar", id: decodeURIComponent(id) };
  if (vista === "entidad" && id && UUID_RE.test(id)) {
    const actor = extra === "actor" && partes[3] ? decodeURIComponent(partes[3]) : undefined;
    return actor ? { vista: "entidad", id, actor } : { vista: "entidad", id };
  }
  if (vista === "registro" && id && extra && partes.length === 3) {
    const runId = decodificar(id), actor = decodificar(extra);
    if (runId && actor && CORRIDA_RE.test(runId) && ACTOR_RE.test(actor)) return { vista: "registro", id: actor, runId };
  }
  if (vista === "representante" && id && UUID_RE.test(id) && partes.length === 2) return { vista: "representante", id: id.toLowerCase() };
  if (vista === "red" && id && UUID_RE.test(id)) {
    const saltos = Number(extra);
    return { vista: "red", id, saltos: saltos >= 1 && saltos <= 3 ? saltos : 2 };
  }
  return { vista: "panorama" };
}
